import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NodeFormBuilder } from '../../../../../../../shared/models';
import { newStepValue } from '../../../../../services/flowBuilder/utils/constants';
import { required } from '../../../../../../../shared/utils';

export interface ChatHubIntegrationNodeForm {
    providerId: string;
    instanceId: string;
    messageEndpoint: string;
    messageParameterId: string;
    mediaEndpoint: string;
    mediaParameterId: string;
    agentConnectMessage: string;
    chatCancelMessage: string;
    next: string;
    nextDescription: string;
    error: string;
    errorDescription: string;
}

export const NODE_TYPE = 'chatHubIntegration';

export class ChatHubIntegrationNode extends NodeFormBuilder<ChatHubIntegrationNodeForm> {

    constructor() {
        super('menu', NODE_TYPE);
    }

    getFromData(stepData: { [index: string]: any }): ChatHubIntegrationNodeForm {
        const menu = stepData?.menu ?? {};
        const chatHubIntegration = menu?.chatHubIntegration ?? menu?.chatHub ?? {};
        const results = menu?.results ?? {};

        return {
            providerId: chatHubIntegration?.providerId ?? '',
            instanceId: chatHubIntegration?.instanceId ?? '',
            messageEndpoint: chatHubIntegration?.messageEndpoint ?? '',
            messageParameterId: chatHubIntegration?.messageParameterId ?? '',
            mediaEndpoint: chatHubIntegration?.mediaEndpoint ?? '',
            mediaParameterId: chatHubIntegration?.mediaParameterId ?? '',
            agentConnectMessage: chatHubIntegration?.agentConnectMessage ?? '',
            chatCancelMessage: chatHubIntegration?.chatCancelMessage ?? '',
            next: results?.next?.next ?? newStepValue,
            nextDescription: results?.next?.description ?? 'Next',
            error: results?.error?.next ?? newStepValue,
            errorDescription: results?.error?.description ?? 'Error',
        };
    }

    buildForm(formData: ChatHubIntegrationNodeForm, editFlows: boolean): UntypedFormGroup {
        return new UntypedFormGroup({
            providerId: new UntypedFormControl({ value: formData.providerId, disabled: !editFlows }, required('Provider')),
            instanceId: new UntypedFormControl({ value: formData.instanceId, disabled: true }),
            messageEndpoint: new UntypedFormControl({ value: formData.messageEndpoint, disabled: !editFlows }, required('Message Endpoint')),
            messageParameterId: new UntypedFormControl({ value: formData.messageParameterId, disabled: !editFlows }, required('Message Parameter')),
            mediaEndpoint: new UntypedFormControl({ value: formData.mediaEndpoint, disabled: !editFlows }, required('Media Endpoint')),
            mediaParameterId: new UntypedFormControl({ value: formData.mediaParameterId, disabled: !editFlows }, required('Media Parameter')),
            agentConnectMessage: new UntypedFormControl({ value: formData.agentConnectMessage, disabled: !editFlows }),
            chatCancelMessage: new UntypedFormControl({ value: formData.chatCancelMessage, disabled: !editFlows }),
            next: new UntypedFormControl(formData.next),
            nextDescription: new UntypedFormControl(formData.nextDescription),
            error: new UntypedFormControl(formData.error),
            errorDescription: new UntypedFormControl(formData.errorDescription),
        });
    }

    setNodeData(nodeData: ChatHubIntegrationNodeForm & { [key: string]: any }, messageParams: { id: string, parameterType: string, isArray: boolean, required: boolean }[] = [], mediaParams: { id: string, parameterType: string, isArray: boolean, required: boolean }[] = [], providerName = ''): void {
        const { providerId, instanceId, messageEndpoint, messageParameterId, mediaEndpoint, mediaParameterId, agentConnectMessage, chatCancelMessage, next, nextDescription, error, errorDescription, ...dynamicParams } = nodeData;

        const messageParameters: { [key: string]: { type: string, value: any, isArray: boolean, required: boolean } } = {};
        const mediaParameters: { [key: string]: { type: string, value: any, isArray: boolean, required: boolean } } = {};

        for (const [key, value] of Object.entries(dynamicParams)) {
            if (key.startsWith('msg_')) {
                const paramId = key.slice(4);
                const meta = messageParams.find(p => p.id === paramId);
                messageParameters[paramId] = { type: meta?.parameterType ?? 'STRING', value: value || null, isArray: meta?.isArray ?? false, required: meta?.required ?? false };
            } else if (key.startsWith('media_')) {
                const paramId = key.slice(6);
                const meta = mediaParams.find(p => p.id === paramId);
                mediaParameters[paramId] = { type: meta?.parameterType ?? 'STRING', value: value || null, isArray: meta?.isArray ?? false, required: meta?.required ?? false };
            }
        }

        const isZendesk = /^zendesk\s+(conversations|support)$/i.test(providerName.trim());

        this.menu = {
            ...this.menu,
            chatHubIntegration: {
                providerId,
                ...(isZendesk ? { providerType: 'zendesk' } : {}),
                instanceId,
                messageEndpoint,
                messageParameterId,
                mediaEndpoint,
                mediaParameterId,
                agentConnectMessage,
                chatCancelMessage,
                messageParameters,
                mediaParameters,
            },
            results: {
                next: { next, description: nextDescription },
                error: { next: error, description: errorDescription },
            },
        };
    }
}
